import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import ui from "../../pages/dashboard/DashboardUI.module.css";
import detailStyles from "../forms/DetailsModal.module.css";
import {
  getProjectAssignments,
  assignEmployeesToProject,
  getProjectStatuses,
} from "../../services/projectService";
import { getEmployees, getProjectsForEmployee } from "../../services/peopleService";

/**
 * ProjectAssignmentModal — Large modal for the Schedule page that lets users:
 * 1. Search for unassigned projects and assign the booking employee
 * 2. View a list of projects the employee is currently assigned to (with roles)
 * 3. Click any project to see full details + an editable Teams tab
 *
 * Props:
 *   employeeId      – The booking employee's ID
 *   employeeName    – The booking employee's display name
 *   employeeJobTitle – The booking employee's job title
 *   allProjects     – Full list of company projects (with isAssigned flag)
 *   currentUser     – The logged-in user object (company_id, user_id)
 *   onAssigned      – Callback(projectId) when the employee is newly assigned to a project
 *   onClose         – Close the modal
 *   showToast       – Toast notification helper
 */
export default function ProjectAssignmentModal({
  employeeId,
  employeeName,
  employeeJobTitle,
  allProjects: externalAllProjects,
  currentUser,
  onAssigned,
  onClose,
  showToast,
}) {
  // ── View state ──────────────────────────────────────────────────
  const [view, setView] = useState("list"); // "list" | "detail"
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState("details");

  // ── Project list state ──────────────────────────────────────────
  const [projectSearch, setProjectSearch] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [assignedProjects, setAssignedProjects] = useState([]); // from getProjectsForEmployee
  const [statuses, setStatuses] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  // ── Team tab state ──────────────────────────────────────────────
  const [teamMembers, setTeamMembers] = useState([]); // assigned employees for selected project
  const [availableEmployees, setAvailableEmployees] = useState([]); // all company employees
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [teamSearchFocused, setTeamSearchFocused] = useState(false);
  const [savingTeam, setSavingTeam] = useState(false);
  const [teamDirty, setTeamDirty] = useState(false);

  // Track newly assigned project for auto-populate on close
  const [newlyAssignedProjectId, setNewlyAssignedProjectId] = useState(null);

  // ── Initial data load ──────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingList(true);
      try {
        const [empProjects, statusData, employeesData] = await Promise.all([
          getProjectsForEmployee(employeeId),
          getProjectStatuses(),
          getEmployees(),
        ]);
        if (cancelled) return;
        setAssignedProjects(empProjects || []);
        setStatuses(statusData?.statuses || statusData || []);
        const empList = employeesData.employees || employeesData || [];
        setAvailableEmployees(
          empList.map((e) => ({
            id: e.user_id || e.employee_id,
            name: e.name,
            job_title: e.job_title,
          }))
        );
      } catch (err) {
        console.error("Failed to load project assignment data", err);
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [employeeId]);

  // ── Helpers ────────────────────────────────────────────────────
  const assignedProjectIds = useMemo(
    () => new Set(assignedProjects.map((p) => p.project_id?.toString())),
    [assignedProjects]
  );

  const allProjectsList = useMemo(
    () => externalAllProjects || [],
    [externalAllProjects]
  );

  const unassignedProjects = useMemo(
    () => allProjectsList.filter((p) => !assignedProjectIds.has(p.project_id?.toString())),
    [allProjectsList, assignedProjectIds]
  );

  const companyOptions = useMemo(() => {
    const seen = new Set();
    const names = [];
    for (const p of unassignedProjects) {
      const name = p.client_company || p.company_name || "";
      if (name && !seen.has(name)) { seen.add(name); names.push(name); }
    }
    return names.sort();
  }, [unassignedProjects]);

  const filteredSearchProjects = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    return unassignedProjects.filter((p) => {
      const company = p.client_company || p.company_name || "";
      if (selectedCompany && company !== selectedCompany) return false;
      if (!q) return true;
      return (
        (p.project_name || "").toLowerCase().includes(q) ||
        company.toLowerCase().includes(q)
      );
    });
  }, [unassignedProjects, projectSearch, selectedCompany]);

  const getStatusName = (statusId) => {
    const s = statuses.find((st) => st.status_id === statusId);
    return s?.name || "No Status";
  };

  const getStatusColor = (statusId) => {
    const s = statuses.find((st) => st.status_id === statusId);
    return s?.color || "#6b7280";
  };

  // ── Open project detail ────────────────────────────────────────
  const openProjectDetail = useCallback(
    async (project, isUnassigned = false) => {
      setSelectedProject(project);
      setView("detail");
      setActiveTab(isUnassigned ? "team" : "details");
      setTeamDirty(false);
      setEmployeeSearch("");

      try {
        const assignments = await getProjectAssignments(project.project_id);
        const employeeList = Array.isArray(assignments)
          ? assignments
          : assignments.assignments || [];
        const mapped = employeeList.map((a) => ({
          id: a.employee_id,
          name: a.user_name,
          role: a.role_name || "",
          job_title: a.job_title,
          isNew: false,
        }));

        // If unassigned project → auto-add the booking employee
        if (isUnassigned) {
          const alreadyInTeam = mapped.find(
            (e) => e.id?.toString() === employeeId?.toString()
          );
          if (!alreadyInTeam) {
            mapped.unshift({
              id: employeeId,
              name: employeeName,
              job_title: employeeJobTitle || "",
              role: "",
              isNew: true,
            });
            setTeamDirty(true);
          } else {
            alreadyInTeam.isNew = true;
          }
        }
        setTeamMembers(mapped);
      } catch (err) {
        console.error("Failed to load project assignments", err);
        // At minimum add the booking employee when opening unassigned
        if (isUnassigned) {
          setTeamMembers([
            {
              id: employeeId,
              name: employeeName,
              job_title: employeeJobTitle || "",
              role: "",
              isNew: true,
            },
          ]);
          setTeamDirty(true);
        } else {
          setTeamMembers([]);
        }
      }
    },
    [employeeId, employeeName, employeeJobTitle]
  );

  // ── Save team ──────────────────────────────────────────────────
  const handleSaveTeam = async () => {
    if (!selectedProject) return;
    setSavingTeam(true);
    try {
      await assignEmployeesToProject(selectedProject.project_id, {
        projectId: selectedProject.project_id,
        clientId: null,
        assignedBy: currentUser.user_id,
        employees: teamMembers.map((emp) => ({
          user_id: emp.id,
          employee_id: emp.id,
          role: emp.role || "",
        })),
      });

      showToast("Team members assigned successfully", "success");

      // Check if the booking employee is now on the team
      const empOnTeam = teamMembers.find(
        (e) => e.id?.toString() === employeeId?.toString()
      );
      if (empOnTeam) {
        setNewlyAssignedProjectId(selectedProject.project_id);
      }

      // Refresh assigned projects list
      const updatedAssigned = await getProjectsForEmployee(employeeId);
      setAssignedProjects(updatedAssigned || []);

      // Clear dirty flag and isNew badges
      setTeamDirty(false);
      setTeamMembers((prev) =>
        prev.map((e) => ({ ...e, isNew: false }))
      );
    } catch (err) {
      console.error("Failed to assign employees to project", err);
      showToast(err.message || "Failed to assign team members", "error");
    } finally {
      setSavingTeam(false);
    }
  };

  // ── Back to list ───────────────────────────────────────────────
  const handleBackToList = () => {
    setView("list");
    setSelectedProject(null);
    setTeamMembers([]);
    setTeamDirty(false);
    setEmployeeSearch("");
  };

  // ── Close modal ────────────────────────────────────────────────
  const handleClose = () => {
    if (newlyAssignedProjectId) {
      onAssigned(newlyAssignedProjectId);
    }
    onClose();
  };

  // ── Render: List View ──────────────────────────────────────────
  const renderListView = () => (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Search bar for unassigned projects */}
      <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb" }}>
        <label
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: "0.8rem",
            fontWeight: "600",
            color: "#374151",
            textTransform: "uppercase",
            letterSpacing: "0.03em",
          }}
        >
          Assign to a new project
        </label>

        {/* Company filter */}
        <select
          value={selectedCompany}
          onChange={(e) => setSelectedCompany(e.target.value)}
          style={{
            width: "100%",
            padding: "9px 14px",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            fontSize: "0.875rem",
            color: selectedCompany ? "#111827" : "#9ca3af",
            background: "white",
            marginBottom: "8px",
            cursor: "pointer",
            boxSizing: "border-box",
          }}
        >
          <option value="">All companies</option>
          {companyOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <div style={{ position: "relative" }}>
          <input
            type="text"
            placeholder="Search projects..."
            value={projectSearch}
            onChange={(e) => setProjectSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
            style={{
              width: "100%",
              padding: "10px 14px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "0.875rem",
              outline: "none",
              boxSizing: "border-box",
            }}
          />

          {/* Search results dropdown */}
          {searchFocused && filteredSearchProjects.length > 0 && (
            <div
              className={ui.searchResults}
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 10,
                maxHeight: "200px",
                overflowY: "auto",
                marginTop: "4px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              {filteredSearchProjects.map((project) => (
                <div
                  key={project.project_id}
                  className={ui.searchItem}
                  onMouseDown={() => {
                    setProjectSearch("");
                    setSearchFocused(false);
                    openProjectDetail(project, true);
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{project.project_name}</span>
                  <span style={{ color: "#6b7280", fontSize: "0.8rem", marginLeft: "8px" }}>
                    {project.client_company || project.company_name || "No Client"}
                  </span>
                </div>
              ))}
            </div>
          )}

          {searchFocused && filteredSearchProjects.length === 0 && (projectSearch.trim() || selectedCompany) && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 10,
                marginTop: "4px",
                padding: "12px 16px",
                background: "white",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                color: "#9ca3af",
                fontSize: "0.85rem",
                fontStyle: "italic",
              }}
            >
              No unassigned projects found
            </div>
          )}
        </div>
      </div>

      {/* Assigned projects list */}
      <div style={{ padding: "16px 24px 0 24px" }}>
        <label
          style={{
            display: "block",
            marginBottom: "12px",
            fontSize: "0.8rem",
            fontWeight: "600",
            color: "#374151",
            textTransform: "uppercase",
            letterSpacing: "0.03em",
          }}
        >
          Currently assigned projects
          {assignedProjects.length > 0 && (
            <span
              style={{
                marginLeft: "8px",
                backgroundColor: "#e5e7eb",
                color: "#374151",
                padding: "2px 8px",
                borderRadius: "10px",
                fontSize: "0.75rem",
                fontWeight: "500",
              }}
            >
              {assignedProjects.length}
            </span>
          )}
        </label>
      </div>

      <div style={{ overflowY: "auto", maxHeight: "320px", padding: "0 24px 16px" }}>
        {loadingList ? (
          <div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af" }}>
            Loading projects...
          </div>
        ) : assignedProjects.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {assignedProjects.map((project) => (
              <div
                key={project.project_id}
                onClick={() => openProjectDetail(project, false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "14px 16px",
                  backgroundColor: "#f9fafb",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f4ff";
                  e.currentTarget.style.borderColor = "#185FA5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                {/* Project color indicator */}
                <div
                  style={{
                    width: "6px",
                    height: "40px",
                    borderRadius: "3px",
                    backgroundColor: project.project_color || project.color || "#185FA5",
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 500,
                      color: "#111827",
                      fontSize: "0.9rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {project.project_name}
                  </div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#6b7280",
                      marginTop: "2px",
                    }}
                  >
                    {project.employee_role || "No role assigned"}
                  </div>
                </div>
                {/* Status badge */}
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: "12px",
                    fontSize: "0.7rem",
                    fontWeight: "500",
                    backgroundColor: (project.status_color || "#6b7280") + "20",
                    color: project.status_color || "#6b7280",
                    flexShrink: 0,
                  }}
                >
                  {project.status_name || "No Status"}
                </span>
                {/* Chevron */}
                <span style={{ color: "#9ca3af", fontSize: "1rem", flexShrink: 0 }}>›</span>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "32px 0",
              color: "#9ca3af",
              fontStyle: "italic",
            }}
          >
            Not assigned to any projects yet
          </div>
        )}
      </div>
    </div>
  );

  // ── Render: Detail View ────────────────────────────────────────
  const renderDetailView = () => {
    if (!selectedProject) return null;

    const project = selectedProject;
    const statusName = getStatusName(project.status_id);
    const statusColor = getStatusColor(project.status_id);

    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Back button + tabs */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 8px",
            borderBottom: "1px solid #e5e7eb",
            background: "#f8fafc",
          }}
        >
          <button
            type="button"
            onClick={handleBackToList}
            style={{
              background: "none",
              border: "none",
              fontSize: "0.85rem",
              color: "#185FA5",
              cursor: "pointer",
              padding: "12px 8px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontWeight: 500,
            }}
          >
            ← Back
          </button>
          <div style={{ display: "flex", gap: 0, flex: 1, paddingLeft: "8px" }}>
            {[
              { key: "details", label: "Details" },
              { key: "team", label: "Team" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`${detailStyles.tabButton} ${
                  activeTab === tab.key ? detailStyles.activeTab : ""
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
          {activeTab === "details" && renderDetailsTab(project, statusName, statusColor)}
          {activeTab === "team" && renderTeamTab()}
        </div>

        {/* Footer actions for team tab */}
        {activeTab === "team" && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              padding: "16px 24px",
              borderTop: "1px solid #e2e8f0",
              background: "#f8fafc",
            }}
          >
            <button
              type="button"
              className={ui.cancelButton}
              onClick={handleBackToList}
            >
              Cancel
            </button>
            <button
              type="button"
              className={ui.saveButton}
              disabled={savingTeam || !teamDirty}
              onClick={handleSaveTeam}
              style={
                savingTeam || !teamDirty
                  ? { opacity: 0.5, cursor: "not-allowed" }
                  : {}
              }
            >
              {savingTeam ? "Saving..." : "Save Team"}
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Details Tab Content ────────────────────────────────────────
  const renderDetailsTab = (project, statusName, statusColor) => (
    <>
      {/* Project Header Banner */}
      <div className={detailStyles.detailSection}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            padding: "20px",
            background: `linear-gradient(135deg, ${
              project.project_color || project.color || "#378ADD"
            } 0%, ${project.project_color || project.color || "#378ADD"}dd 100%)`,
            borderRadius: "12px",
            marginBottom: "8px",
          }}
        >
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.2)",
              border: "2px solid rgba(255,255,255,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
              flexShrink: 0,
            }}
          >
            📋
          </div>
          <div style={{ flex: 1 }}>
            <h3
              style={{
                margin: 0,
                fontSize: "1.25rem",
                fontWeight: "600",
                color: "white",
              }}
            >
              {project.project_name || "Untitled Project"}
            </h3>
            <p
              style={{
                margin: "4px 0 0",
                color: "rgba(255,255,255,0.85)",
                fontSize: "0.9rem",
              }}
            >
              {project.company_name || project.client_company || project.client_name || "No client"}
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "6px",
            }}
          >
            <span
              style={{
                backgroundColor:
                  project.urgency === "High"
                    ? "#ef4444"
                    : project.urgency === "Medium"
                    ? "#f59e0b"
                    : "#22c55e",
                color: "white",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "0.75rem",
                fontWeight: "600",
                textTransform: "uppercase",
              }}
            >
              {project.urgency || "Low"}
            </span>
            <span
              style={{
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "white",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "0.75rem",
                fontWeight: "500",
              }}
            >
              {statusName}
            </span>
          </div>
        </div>
      </div>

      {/* Client Information */}
      <div className={detailStyles.detailSection}>
        <h4 className={detailStyles.sectionTitle}>Client Information</h4>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Client Name</span>
          <span
            className={`${detailStyles.detailValue} ${
              !project.client_name ? detailStyles.empty : ""
            }`}
          >
            {project.client_name || "—"}
          </span>
        </div>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Company</span>
          <span
            className={`${detailStyles.detailValue} ${
              !(project.company_name || project.client_company)
                ? detailStyles.empty
                : ""
            }`}
          >
            {project.company_name || project.client_company || "—"}
          </span>
        </div>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Email</span>
          <span
            className={`${detailStyles.detailValue} ${
              !project.email ? detailStyles.empty : ""
            }`}
          >
            {project.email ? (
              <a
                href={`mailto:${project.email}`}
                style={{ color: "#185FA5", textDecoration: "none" }}
              >
                {project.email}
              </a>
            ) : (
              "—"
            )}
          </span>
        </div>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Phone</span>
          <span
            className={`${detailStyles.detailValue} ${
              !project.phone_number ? detailStyles.empty : ""
            }`}
          >
            {project.phone_number || "—"}
          </span>
        </div>
      </div>

      {/* Project Details */}
      <div className={detailStyles.detailSection}>
        <h4 className={detailStyles.sectionTitle}>Project Details</h4>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Timeline</span>
          <span className={detailStyles.detailValue}>
            {project.start_date || project.end_date ? (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span
                  style={{
                    backgroundColor: "#E6F1FB",
                    color: "#0C447C",
                    padding: "4px 10px",
                    borderRadius: "6px",
                    fontSize: "0.85rem",
                    fontWeight: "500",
                  }}
                >
                  {project.start_date?.toString().slice(0, 10) || "No start"}
                </span>
                <span style={{ color: "#9ca3af" }}>→</span>
                <span
                  style={{
                    backgroundColor: "#dcfce7",
                    color: "#166534",
                    padding: "4px 10px",
                    borderRadius: "6px",
                    fontSize: "0.85rem",
                    fontWeight: "500",
                  }}
                >
                  {project.end_date?.toString().slice(0, 10) || "No end"}
                </span>
              </div>
            ) : (
              <em className={detailStyles.empty}>No dates set</em>
            )}
          </span>
        </div>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Location</span>
          <span
            className={`${detailStyles.detailValue} ${
              !project.location ? detailStyles.empty : ""
            }`}
          >
            {project.location || "—"}
          </span>
        </div>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Address</span>
          <span
            className={`${detailStyles.detailValue} ${
              !project.address ? detailStyles.empty : ""
            }`}
          >
            {project.address ? (
              <div className={detailStyles.descriptionText}>{project.address}</div>
            ) : (
              "—"
            )}
          </span>
        </div>

        <div className={detailStyles.detailRow}>
          <span className={detailStyles.detailLabel}>Description</span>
          <span
            className={`${detailStyles.detailValue} ${
              !project.description ? detailStyles.empty : ""
            }`}
          >
            {project.description ? (
              <div className={detailStyles.descriptionText}>
                {project.description}
              </div>
            ) : (
              "No description"
            )}
          </span>
        </div>
      </div>
    </>
  );

  // ── Team Tab Content ───────────────────────────────────────────
  const renderTeamTab = () => (
    <div>
      {/* Employee search */}
      <div className={ui.inputGroup}>
        <label>Search people by name or job title</label>
        <input
          type="text"
          placeholder="Search..."
          value={employeeSearch}
          onChange={(e) => setEmployeeSearch(e.target.value)}
          onFocus={() => setTeamSearchFocused(true)}
          onBlur={() => setTimeout(() => setTeamSearchFocused(false), 200)}
        />
      </div>

      {teamSearchFocused && (
        <div className={ui.searchResults}>
          {availableEmployees
            .filter((emp) => {
              const searchText = `${emp.name} ${emp.job_title || ""}`.toLowerCase();
              return searchText.includes(employeeSearch.toLowerCase());
            })
            .filter(
              (emp) =>
                !teamMembers.some(
                  (a) => a.id?.toString() === emp.id?.toString()
                )
            )
            .map((emp) => (
              <div
                key={emp.id}
                className={ui.searchItem}
                onMouseDown={() => {
                  setTeamMembers((prev) => [
                    ...prev,
                    { ...emp, role: "", isNew: false },
                  ]);
                  setEmployeeSearch("");
                  setTeamDirty(true);
                }}
              >
                {emp.name} — {emp.job_title || ""}
              </div>
            ))}
        </div>
      )}

      {/* Assigned team members list */}
      {teamMembers.length > 0 ? (
        <div className={ui.assignedList}>
          <h4>
            Assigned Team Members
            <span
              style={{
                marginLeft: "8px",
                backgroundColor: "#e5e7eb",
                color: "#374151",
                padding: "2px 8px",
                borderRadius: "10px",
                fontSize: "0.75rem",
                fontWeight: "500",
              }}
            >
              {teamMembers.length}
            </span>
          </h4>
          {teamMembers.map((emp, idx) => (
            <div
              key={emp.id}
              className={ui.assignedItem}
              style={
                emp.isNew
                  ? {
                      background:
                        "linear-gradient(90deg, #dcfce7 0%, transparent 100%)",
                      borderLeft: "3px solid #22c55e",
                      paddingLeft: "12px",
                    }
                  : {}
              }
            >
              <div className={ui.employeeInfo}>
                <span className={ui.employeeName}>
                  {emp.name}
                  {emp.isNew && (
                    <span
                      style={{
                        marginLeft: "8px",
                        fontSize: "0.7rem",
                        background: "#22c55e",
                        color: "white",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontWeight: 500,
                      }}
                    >
                      NEW
                    </span>
                  )}
                </span>
                <span className={ui.employeeTitle}>{emp.job_title || ""}</span>
              </div>
              <input
                type="text"
                placeholder="Role in project"
                value={emp.role || ""}
                onChange={(e) => {
                  const updated = [...teamMembers];
                  updated[idx].role = e.target.value;
                  setTeamMembers(updated);
                  setTeamDirty(true);
                }}
                className={ui.roleInput}
              />
              <button
                type="button"
                className={ui.removeButton}
                onClick={() => {
                  setTeamMembers(teamMembers.filter((e) => e.id !== emp.id));
                  setTeamDirty(true);
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className={ui.emptyState}>
          <p>No team members assigned yet</p>
        </div>
      )}
    </div>
  );

  // ── Main render ────────────────────────────────────────────────
  return ReactDOM.createPortal(
    <div className={ui.modalOverlay}>
      <div
        className={ui.modal}
        style={{
          maxWidth: "640px",
          minHeight: "500px",
          maxHeight: "85vh",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderBottom: "1px solid #e5e7eb",
            background: "#f9fafb",
            borderRadius: "16px 16px 0 0",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "1.125rem",
                fontWeight: 600,
                color: "#111827",
              }}
            >
              {view === "list" ? "Project Assignment" : selectedProject?.project_name || "Project"}
            </h2>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: "0.8rem",
                color: "#6b7280",
              }}
            >
              {employeeName}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              color: "#6b7280",
              cursor: "pointer",
              padding: "0",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {view === "list" ? renderListView() : renderDetailView()}
        </div>

        {/* Footer for list view (just a close button) */}
        {view === "list" && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "16px 24px",
              borderTop: "1px solid #e2e8f0",
              background: "#f8fafc",
              borderRadius: "0 0 16px 16px",
            }}
          >
            <button
              type="button"
              className={ui.cancelButton}
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
